import TemplateDashboardRepository from "../../api/repositories/templateDashboard";
import ModuleMeta from "@/_helpers/ModuleMeta";
import CsharpEnum from "@/_helpers/CsharpEnum";
import { globalWidgetTypes } from "@/_helpers/templateDashboardHelper";

function getGlobalValues(globalWidgets, globalValues = {}) {
  let res = {};
  for (var widget of globalWidgets) {
    switch (widget.widgetType) {
      case CsharpEnum.ModuleType.MASTERTIMEPICKER:
        res.globalTimeRange = {
          start: widget.meta.find(
            (x) => x.key == ModuleMeta.Enum.GLOBAL_TIMERANGE_START
          )?.value,
          end: widget.meta.find(
            (x) => x.key == ModuleMeta.Enum.GLOBAL_TIMERANGE_END
          )?.value,
        };
        break;
      default:
        continue;
    }
  }
  res.dashboardZoom = globalValues.dashboardZoom ?? {
    start: 0,
    end: 100,
  };
  return res;
}

export const templateDashboard = {
  namespaced: true,

  state: {
    dashboard: {},
    widgets: {},
    dashboards: [],
    globalWidgets: [],
    globalValues: {},
  },

  actions: {
    saveDashboardToState({ commit }, dashboard) {
      commit("SET_DASHBOARD", dashboard);
    },

    removeWidgetFromState({ commit }, widgetId) {
      commit("REMOVE_WIDGET", widgetId);
    },

    removeGlobalWidgetFromState({ commit }, widgetId) {
      commit("REMOVE_GLOBAL_WIDGET", widgetId);
    },

    reset({ commit }) {
      commit("RESET_DASHBOARD");
    },

    async getTemplateDashboard({ commit }, templateDashboardId) {
      await TemplateDashboardRepository.getDashboard(templateDashboardId)
        .then((d) => commit("SET_DASHBOARD", d.data))
        .catch((e) => console.error(e));
    },

    async getTemplateDashboards({ commit }, commitToStore = true) {
      let result = await TemplateDashboardRepository.getDashboards()
        .then((d) => d.data)
        .catch((e) => {
          console.error(e);
        });
      if (result !== undefined && commitToStore)
        commit("SET_TEMPLATE_DASHBOARDS", result);

      return result;
    },

    async getDashboardWidgets({ commit }, templateDashboardId) {
      let result = await TemplateDashboardRepository.getWidgets(
        templateDashboardId
      )
        .then((d) => d.data)
        .catch((e) => {
          console.error(e);
        });

      let normalWidgets = [];
      let globalWidgets = [];
      if (result) {
        for (var widget of result)
          globalWidgetTypes.includes(widget?.widgetType)
            ? globalWidgets.push(widget)
            : normalWidgets.push(widget);
      }

      commit("SET_WIDGETS", normalWidgets);
      commit("SET_GLOBAL_WIDGETS", globalWidgets);
      return result ?? [];
    },

    async getTemplateDashboardItems(_, dashboardId) {
      return await TemplateDashboardRepository.getTemplateDashboardItems(
        dashboardId
      )
        .then((d) => d.data)
        .catch((e) => {
          console.error(e);
          return [];
        });
    },

    async saveTemplateDashboardItems(_, { dashboardId, payload }) {
      return await TemplateDashboardRepository.updateItems(dashboardId, payload)
        .then(() => true)
        .catch((e) => {
          console.error(e);
          return false;
        });
    },

    setWidgets({ commit }, widgets) {
      commit("SET_WIDGETS", widgets);
    },

    setGlobalWidgets({ commit }, widgets) {
      commit("SET_GLOBAL_WIDGETS", widgets);
    },

    addOrUpdateWidget({ commit }, widget) {
      commit("ADD_OR_UPDATE_WIDGET", widget);
    },

    updateGlobalWidget({ commit }, widget) {
      commit("UPDATE_GLOBAL_WIDGET", widget);
    },

    // payload: {widgetType,  metaKeysAndValues: [{metaKey: , value: },]}
    updateGlobalValues({ commit }, payload) {
      commit("UPDATE_GLOBAL_VALUES", payload);
    },

    // Should return the templateDashboardId
    async createTemplateDashboard(_, payload) {
      var result = await TemplateDashboardRepository.create(payload)
        .then((d) => [true, d.data])
        .catch((d) => [false, d]);

      if (!result[0]) {
        console.error(result[1]);
        return undefined;
      }

      // result[1] should contain a string of the guid for Template dashboard ID
      return result[1];
    },

    async updateTemplateDashboard(_, { templateDashboardId, payload }) {
      return await TemplateDashboardRepository.update(
        templateDashboardId,
        payload
      )
        .then(() => true)
        .catch(() => false);
    },

    async saveTemplateDashboardWidgets({ getters }, payload) {
      // Get the current template dashboard id
      let tId = getters.templateDashboardId;
      if (tId === undefined) {
        console.error("No template dashboard ID was found in store");
        return;
      }

      return await TemplateDashboardRepository.updateWidgets(tId, payload)
        .then(() => [true, "Ok!"])
        .catch((d) => [false, d]);
    },

    async deleteTemplateDashboard(_, templateDashboardId) {
      return await TemplateDashboardRepository.delete(templateDashboardId)
        .then(() => true)
        .catch((d) => {
          console.error(d);
          return false;
        });
    },
  },

  mutations: {
    REMOVE_WIDGET(state, id) {
      let widgets = state.widgets;
      if (widgets === undefined) return;

      let idx = widgets.findIndex((d) => d.templateWidgetId === id);
      if (idx !== -1) widgets.splice(idx, 1);

      // Index widgets
      let i = 0;
      for (var w of state.widgets) {
        w.i = i++;
      }
    },

    REMOVE_GLOBAL_WIDGET(state, id) {
      let widgets = state.globalWidgets;
      if (widgets === undefined) return;

      let idx = widgets.findIndex((d) => d.templateWidgetId === id);
      if (idx !== -1) {
        widgets.splice(idx, 1);
        state.globalValues = getGlobalValues(widgets, state.globalValues);
      }

      // Index widgets
      let i = 0;
      for (var w of state.globalWidgets) {
        w.i = i++;
      }
    },

    SET_DASHBOARD(state, dashboard) {
      state.dashboard = dashboard;
    },

    RESET_DASHBOARD(state) {
      state.dashboard = {};
      state.widgets = [];
      state.globalWidgets = [];
      state.globalValues = {};
    },

    SET_TEMPLATE_DASHBOARDS(state, val) {
      state.dashboards = val;
    },

    SET_WIDGETS(state, val) {
      state.widgets = val;
    },

    SET_GLOBAL_WIDGETS(state, val) {
      state.globalWidgets = val;
      state.globalValues = getGlobalValues(val, state.globalValues);
    },

    SET_DASHBOARD_ZOOM(state, val) {
      state.globalValues.dashboardZoom = val;
    },

    ADD_OR_UPDATE_WIDGET(state, val) {
      if (state.widgets?.length === undefined) state.widgets = [];

      // Normal widget
      let widgetIdx = state.widgets.findIndex(
        (d) => d.templateWidgetId === val.templateWidgetId
      );
      if (widgetIdx !== -1) {
        // Update the widget instead
        state.widgets[widgetIdx] = val;
        return;
      }

      // Global widget. Only 1 of each Global-widgetType
      widgetIdx = state.globalWidgets.findIndex(
        (x) => x.widgetType == val.widgetType
      );
      if (widgetIdx !== -1) {
        // Update the widget instead
        state.globalWidgets[widgetIdx] = val;
        state.globalValues = getGlobalValues(
          state.globalWidgets,
          state.globalValues
        );
        return;
      }

      if (globalWidgetTypes.includes(val.widgetType)) {
        state.globalWidgets.push(val);
        state.globalValues = getGlobalValues(
          state.globalWidgets,
          state.globalValues
        );
      } else {
        state.widgets.push(val);
      }

      // Index widgets
      let i = 0;
      for (var w of state.widgets) {
        w.i = i++;
      }
      i = 0;
      for (var w of state.globalWidgets) {
        w.i = i++;
      }
    },

    UPDATE_GLOBAL_WIDGET(state, val) {
      let widgetIdx = state.globalWidgets.findIndex(
        (x) => x.widgetType == val.widgetType
      );
      if (widgetIdx !== -1) {
        state.globalWidgets[widgetIdx] = val;
        state.globalValues = getGlobalValues(
          state.globalWidgets,
          state.globalValues
        );
      }
    },

    UPDATE_GLOBAL_VALUES(state, payload) {
      let widgetIdx = state.globalWidgets.findIndex(
        (x) => x.widgetType == payload.widgetType
      );
      if (widgetIdx !== -1) {
        let widget = state.globalWidgets[widgetIdx];
        for (var kv of payload.metaKeysAndValues) {
          let meta = widget.meta.find((x) => x.key == kv.metaKey);
          if (meta == undefined) continue;
          meta.value = kv.value;
        }
        state.globalValues = getGlobalValues(
          state.globalWidgets,
          state.globalValues
        );
      }
    },
  },

  getters: {
    templateDashboardId(state) {
      return state.dashboard?.templateDashboardId ?? undefined;
    },
  },
};
